import React, { useState, useEffect } from 'react';
import { Grid, Typography, TextField, Stack, Select, MenuItem } from "@mui/material";
import { useNavigate } from 'react-router';
import Spinner from '../Spinner';
import '../../scss/ClientForm.scss';
import intl from 'components/i18n/ReactIntlWrapper';
import NewInvoiceItemTable from './NewInvoiceItemTable'
import NewInvoiceSummary from './NewInvoiceSummary'
import Chip from "@mui/material/Chip";
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useNewInvoice } from './newInvoiceProvider';
import { useApi } from 'api/ApiProvider';
import Api from "axiosApi/api";
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import WorkflowActionsBar from '../Workflow/WorkflowActionsBar';
import { useWorkflow } from 'components/Workflow/WorkflowContext';
import { handleAPIError } from '../../common/errorHandler';
import NewInvoiceItemTableView from './NewInvoiceItemTableView';
import NewInvoiceSummaryView from './NewInvoiceSummaryView';

const NewInvoiceForm = ({ disabled, review, view }) => {
    const { currencies, invoiceTypes, billToPersonas, input, setInput, isManualCreation, isBillingAddressFromLoading, isBillingAddressToLoading, isBillToPersonasLoading, isBillFromPersonasLoading, errors, setErrors } = useNewInvoice();
    const [statusLabel, setStatusLabel] = useState<{label: string, color: string}>();
    const [loading, setLoading] = useState<boolean>(false);
    const [workflowLabelKeys, setWorkflowLabelKeys] = useState(null);
    const toast = useToastMessageQueue();
    const api: Api = useApi();
    const navigate = useNavigate();
    const { workflow } = useWorkflow();

    useEffect(() => {
        const newItem = {
            id: 1,
            amount: "",
            unit: 1,
            invoiceItemTaxes: []
        }
        input.invoiceItems = [newItem];
    }, []);

    useEffect(() => {
        setWorkflowLabelKeys(
            workflow?.states?.map(state => ({ label : state.labelKey, id : state.id }))
        )
    },[workflow])

    useEffect(() => {
        const currentStatus = workflowLabelKeys?.filter((labelKey) => labelKey.id === input.idWorkflowState);
        if (!currentStatus?.[0]){
            setStatusLabel({label: "invoicing.manualReview", color: "primary"})
        } else {
            const label = currentStatus[0]?.label;
            const color = label === "Pending" || label === "Pendiente" ? "warning"
                : label === "Approved" || label === "Aprobado" ? "success"
                : label === "Observed" || label === "Observado" ? "default"
                : label === "Open" || label === "Abierto" ? "primary"
                : label === "Reopen" || label === "Reabrir" ? "secondary"
                : "error";
            setStatusLabel({label, color});
        }
    },[workflowLabelKeys, input?.idWorkflowState])

    useEffect(() => {
        const handleAutofill = () => {
            // Get all form inputs
            const inputs = document.querySelectorAll('input, select');
            inputs.forEach(input => {
                const element = input as HTMLInputElement | HTMLSelectElement;
                if (element.value && !element.dataset.initialized) {
                    // Mark as initialized to prevent duplicate handling
                    element.dataset.initialized = 'true';
                    // Trigger change event
                    const event = new Event('change', { bubbles: true });
                    element.dispatchEvent(event);
                }
            });
        };

        // Run on mount and after a short delay to catch autofill
        handleAutofill();
        const timeoutId = setTimeout(handleAutofill, 100);

        return () => clearTimeout(timeoutId);
    }, []);

    const handleInputChange = (name, value) => {
        if (name === "idTo") {
            setInput({
                ...input,
                [name]: value,
                to: billToPersonas.find(persona => persona.id === value).name
            });
        } else {
            setInput({
                ...input,
                [name]: value
            });
        }
        //validate errors
        setErrors(prevErrors => ({
            ...prevErrors,
            [name]: ""
        }));
    };

    const handleSave = async (id, comments) => {
        setLoading(true);
        var result = false;
        try {
            input.comments = comments;
            const errorList = {};
            if (!input.number) errorList['number'] = `${intl.get('newInvoice.numberRequired')}`;
            if (!input.idCurrency || input.idCurrency === 0 || input.idCurrency === "") errorList['idCurrency'] = `${intl.get('newInvoice.currencyRequired')}`;
            if (!input.idInvoiceType || input.idInvoiceType === 0 || input.idInvoiceType === "") errorList['idInvoiceType'] = `${intl.get('newInvoice.isRequired')}`;
            if (!input.date) errorList['date'] = `${intl.get('newInvoice.dateRequired')}`;
            if (!input.addressFrom) errorList['addressFrom'] = `${intl.get('newInvoice.isRequired')}`;
            if (!input.addressTo) errorList['addressTo'] = `${intl.get('newInvoice.isRequired')}`;
            if (!input.idFrom || input.idFrom === 0 || input.idFrom === "") errorList['idFrom'] = `${intl.get('newInvoice.profileRequired')}`;
            if (!input.idTo || input.idTo === 0 || input.idTo === "") errorList['idTo'] = `${intl.get('newInvoice.isRequired')}`;
            if (!input.invoiceItems || input.invoiceItems.length === 0) errorList['invoiceItems'] = `${intl.get('newInvoice.itemsRequired')}`;
            if (!input.attachments && !isManualCreation) errorList['attachments'] = `${intl.get('newInvoice.isRequired')}`;
            input.invoiceItems.forEach(item => {
                if (item.description === "" || !item.description || item.quantity === "" || !item.quantity || item.rate === "" || !item.rate){
                    errorList['invoiceItems'] = "Missing one or more field in items"
                }
            });
            setErrors(prevErrors => ({ ...prevErrors, ...errorList }));

            const filteredErrors = Object.fromEntries(
                Object.entries(errors).filter(([key, value]) => value !== "missing confidence" && value !== "")
            );
            if (Object.keys(filteredErrors).length > 0) {
                setLoading(false);
                return;
            }

            const inputCopy = { ...input };

            if (inputCopy.invoiceItems) {
                inputCopy.invoiceItems = inputCopy.invoiceItems.map(item => {
                    const newItem = { ...item };
                    newItem.amount = item.rate;

                    if (newItem.invoiceItemTaxes && newItem.invoiceItemTaxes.length > 0) {
                        const itemAmount = (Number(newItem.quantity) || 0) * (Number(newItem.amount) || 0);
                        newItem.invoiceItemTaxes = newItem.invoiceItemTaxes.map(tax => ({
                            idTaxRate: tax.id,
                            taxAmount: tax.amount === 0 ? itemAmount * (Number(tax.rate) / 100) : Number(tax.amount),
                            taxRate: Number(tax.rate) || 0,
                            invoiceItemId: 0,
                            invoiceItem: {}
                        }));
                    }
                    return newItem;
                });
            }

            inputCopy.idWorkflowState = id;
            inputCopy.number = inputCopy.number.toString();
            inputCopy.idInvoiceType = Number(inputCopy.idInvoiceType);

            if (inputCopy.attachments && !inputCopy.fileURI) {
                inputCopy.fileURI = inputCopy.attachments[0].fileURI;
            }

            if (inputCopy.invoice && !review) {
                const reader = new FileReader();
                reader.readAsDataURL(inputCopy.invoice);
                await new Promise(() => {
                    reader.onload = () => {
                        inputCopy.fileURI = reader.result;
                        delete inputCopy.invoice;
                    };
                });
            }

            const formatedInput = {
                ...inputCopy,
                date: new Date(inputCopy.date).toISOString().split('T')[0]
            };
            const response = await api.invoiceApi.apiVversionInvoicePost("1", formatedInput);
            if (response.data?.data) {
                toast.success({ body: intl.get('newInvoice.toast.success.handleSaveAsDraft') });
                navigate(0);
                result = true;
            }
        } catch (error) {
            handleAPIError(error, toast, errors);
            setErrors({ ...errors });
            setLoading(false);
            result = false;
            throw error;
        }
        setLoading(false);
        return result;
    };

    const isDisabled = () => {
        return Object.entries(errors || {}).some(([key, value]) => value && value !== "missing confidence") ||
               !input.number ||
               !input.idCurrency ||
               !input.idInvoiceType ||
               !input.date ||
               !input.addressFrom ||
               !input.addressTo ||
               !input.idFrom ||
               !input.idTo ||
               !input.invoiceItems ||
               input.invoiceItems?.some(item => !item.description || item.description === "" || !item.quantity || item.quantity === "" || !item.amount || item.amount === "") ||
               input.invoiceTaxes?.some(tax => errors?.[`invoiceTaxes[${tax.idTaxRate}].description`] === "missing confidence") ||
               input.invoiceItems?.some(item =>
                   item.invoiceItemTaxes?.some(tax => tax.id === 0 || tax.id === null)
               );
    };

    return (
        <>
            <Grid container xl={12} className='justify-content-around' style={{marginTop: (errors?.number || errors?.idCurrency) && (errors?.number !== "missing confidence" && errors?.idCurrency !== "missing confidence") ? "20px" : "0px"}}>
                {(loading === true || isBillingAddressToLoading || isBillingAddressFromLoading || isBillToPersonasLoading || isBillFromPersonasLoading) && <Spinner />}
                <Grid container xl={6}>
                    <Grid item xl={12} container>
                        <Grid item xs={12} md={5} style={{marginTop: errors?.number && errors?.number !== "missing confidence" ? "-30px" : "0px"}}>
                            <h3 className="title">{intl.get('invoicesList.invoice.number')}</h3>
                        </Grid>
                        <Grid item xs={12} md={7} style={{marginTop: errors?.number && errors?.number !== "missing confidence" ? "-30px" : "0px"}}>
                            {errors?.number && errors?.number !== "missing confidence" && (
                                <Typography className='text-danger'>
                                    {errors?.number}
                                </Typography>
                            )}
                            {view ? (
                                <input 
                                    type="text"
                                    value={input?.number}
                                    disabled={true}
                                    style={{width: '100%', padding: '8px'}}
                                />
                            ) : (
                            <TextField
                                id="number"
                                name="number"
                                placeholder={intl.get('newInvoice.invoiceNumber')}
                                value={input?.number}
                                onChange={(e) => handleInputChange("number", e.target.value)}
                                onBlur={(e) => handleInputChange("number", e.target.value)}
                                disabled={disabled}
                                fullWidth
                                error={errors?.number ? true : false}
                            />
                            )}
                        </Grid>
                    </Grid>
                </Grid>
                <Grid container xl={6}>
                    <Grid container item xl={12} className='justify-content-start'>
                            <Grid item xs={12} md={7} style={{ display: "flex", justifyContent: "flex-end", marginTop: "2px", paddingRight: "10px"}}>
                                <Chip
                                    key="statusChip"
                                    label={intl.get(`${statusLabel?.label}`)}
                                    color={statusLabel?.color as "primary" | "warning" | "success" | "default" | "secondary" | "error" | "info"}
                                    className={statusLabel?.color === "default" ? 'observed' : ''}
                                    variant="filled"
                                    style={{
                                        fontWeight: "bold",
                                        display: (view || review) ? "" : "none"
                                    }}
                                />
                            </Grid>
                        <Grid item xs={12} xl={5} style={{marginTop: errors?.idCurrency && errors?.idCurrency !== "missing confidence" ? "-30px" : "0px"}}>
                            {errors?.idCurrency && errors?.idCurrency !== "missing confidence" && (
                                <Typography className='text-danger'>
                                    {errors?.idCurrency}
                                </Typography>
                            )}
                            {view ? (
                                <input 
                                    type="text"
                                    value={input?.currency?.code || ''}
                                    disabled={true}
                                    style={{width: '100%', padding: '8px'}}
                                />
                            ) : (
                            <Select
                                id='currency'
                                name='currency'
                                value={input?.idCurrency}
                                size='small'
                                displayEmpty
                                fullWidth
                                disabled={disabled}
                                inputProps={{placeholder: intl.get('newInvoice.invoiceCurrency')}}
                                onChange={(e) => {handleInputChange("idCurrency", e.target.value)}}
                                onBlur={(e) => {handleInputChange("idCurrency", e.target.value)}}
                                error={errors?.idCurrency ? true : false}
                            >
                                <MenuItem disabled value="">
                                    <em>{intl.get('newInvoice.invoiceCurrency')}</em>
                                </MenuItem>
                                {currencies?.map((currency) => (
                                    <MenuItem
                                        key={currency.id}
                                        value={currency.id}
                                    >
                                        {currency.code}
                                    </MenuItem>
                                ))}
                            </Select>
                            )}
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            <Grid container xl={12} className='justify-content-around'>
                <Grid container item xl={6}>
                    <Grid container item xl={12}>
                        <Stack className='w-100'>
                            <Typography className={`${errors?.addressFrom ? 'text-danger' : ''}`}>
                                {intl.get('newInvoice.invoiceAddressFrom')} {errors?.addressFrom && errors?.addressFrom !== "missing confidence" && (<span className='text-danger'>{errors?.addressFrom}</span>)}
                            </Typography>
                            {view ? (
                                <input 
                                    type="text"
                                    value={input?.addressFrom}
                                    disabled={true}
                                    style={{width: '100%', padding: '8px'}}
                                />
                            ) : (
                            <TextField
                                id="addressFrom"
                                name="addressFrom"
                                placeholder={intl.get('newInvoice.invoiceAddressFrom')}
                                value={input?.addressFrom}
                                onChange={(e) => handleInputChange("addressFrom", e.target.value)}
                                disabled={isBillingAddressFromLoading || disabled}
                                onBlur={(e) => handleInputChange("addressFrom", e.target.value)}
                                className='w-100'
                                error={errors?.addressFrom ? true : false}
                                />
                            )}
                        </Stack>
                    </Grid>
                </Grid>
                <Grid container item xl={6}>
                    <Grid container item xl={12}  className='justify-content-end' style={{marginTop: "30px", alignContent: "center", display: "flex"}}>
                            <Typography className={`${errors?.date ? 'text-danger' : ''}`} style={{marginTop: "10px"}}>
                                {intl.get('newInvoice.date')} {errors?.date && errors?.date !== "missing confidence" && (<span className='text-danger'>{errors?.date}</span>)}
                            </Typography>
                            <div style={{marginTop: "-6px", marginLeft: "10px"}}>
                                <DemoContainer components={['DatePicker']}>
                                    {view ? (
                                        <input 
                                            type="text"
                                            value={input?.date ? new Date(input?.date).toLocaleDateString() : ''}
                                            disabled={true}
                                            style={{width: '100%', padding: '8px'}}
                                        />
                                    ) : (
                                    <DatePicker
                                        slotProps={{
                                            openPickerIcon: { fontSize: 'small' },
                                            textField: {
                                                size: "small",
                                                error: errors?.date ? true : false
                                            }
                                        }}
                                        disabled={disabled}
                                        className="Invoice.DatePicker"
                                        value={input?.date}
                                        onChange={(e) => handleInputChange("date", e)}
                                        sx={{
                                            '& .MuiInputBase-root': {
                                                overflow: 'hidden',
                                                '&.Mui-error': {
                                                    border: '1px solid red !important'
                                                }
                                            }
                                        }}
                                        />
                                    )}
                                </DemoContainer>
                            </div>
                    </Grid>
                </Grid>
            </Grid>
            <Grid container xl={12} className='justify-content-around'>
                <Grid container xl={6}>
                    <Typography className={`${errors?.idTo ? 'text-danger' : ''}`} style={{marginTop: "5px"}}>
                        {intl.get('newInvoice.to')} {errors?.idTo && (<span className='text-danger'>{errors?.idTo}</span>)}
                    </Typography>
                    <Grid item xl={12}>
                        {view ? (
                            <input 
                                type="text"
                                value={input?.personaTo?.name || ''}
                                disabled={true}
                                style={{width: '100%', padding: '8px'}}
                            />
                        ) : (
                        <Select
                            id='billTo'
                            name='billTo'
                            value={input?.idTo}
                            size='small'
                            fullWidth
                            displayEmpty
                            placeholder={intl.get('newInvoice.to')}
                            disabled={disabled}
                            inputProps={{
                                placeholder: intl.get('newInvoice.to')
                            }}
                            onChange={(e) => handleInputChange("idTo", e.target.value)}
                            onBlur={(e) => handleInputChange("idTo", e.target.value)}
                            error={errors?.idTo ? true : false}
                        >
                            <MenuItem disabled value="">
                                <em>{intl.get('newInvoice.to')}</em>
                            </MenuItem>
                            {billToPersonas?.map((persona) => (
                                <MenuItem
                                    key={persona.id}
                                    value={persona.id}
                                >
                                    {persona.name}
                                </MenuItem>
                            ))}
                            </Select>
                        )}
                    </Grid>
                </Grid>
                <Grid container xl={6}>
                </Grid>
            </Grid>
            <Grid container item xl={12} className='justify-content-around' style={{marginTop: "10px"}}>
                <Grid container item xl={6}>
                    <Stack className='w-100'>
                        <Typography className={`${errors?.addressTo ? 'text-danger' : ''}`}>
                            {intl.get('newInvoice.invoiceAddressTo')} {errors?.addressTo && errors?.addressTo !== "missing confidence" && (<span className='text-danger'>{errors?.addressTo}</span>)}
                        </Typography>
                        {view ? (
                            <input 
                                type="text"
                                value={input?.addressTo}
                                disabled={true}
                                style={{width: '100%', padding: '8px'}}
                            />
                        ) : (
                        <TextField
                            id="addressTo"
                            name="addressTo"
                            placeholder={intl.get('newInvoice.invoiceAddressTo')}
                            value={input?.addressTo}
                            onChange={(e) => handleInputChange("addressTo", e.target.value)}
                            disabled={isBillingAddressToLoading || disabled}
                            onBlur={(e) => handleInputChange("addressTo", e.target.value)}
                            error={errors?.addressTo ? true : false}
                        />
                        )}
                    </Stack>
                </Grid>
                <Grid container xl={6}>
                </Grid>
            </Grid>
            <hr/>
            <Grid container xl={12}>
                <Grid container xl={6}  className='justify-content-start'>
                    <Typography className={`${errors?.idInvoiceType ? 'text-danger' : ''}`} style={{marginTop: "5px"}}>
                        {intl.get('newInvoice.invoiceType')} {errors?.idInvoiceType && errors?.idInvoiceType !== "missing confidence" && (<span className='text-danger'>{errors?.idInvoiceType}</span>)}
                    </Typography>
                    <Grid item xl={12}>
                        {view ? (
                            <input 
                                type="text"
                                value={input?.invoiceType?.name || ''}
                                disabled={true}
                                style={{width: '100%', padding: '8px'}}
                            />
                        ) : (
                        <Select
                            id='invoiceType'
                            name='invoiceType'
                            value={input?.idInvoiceType === 0 ? "" : input?.idInvoiceType}
                            fullWidth
                            displayEmpty
                            disabled={disabled}
                            inputProps={{
                                placeholder: intl.get('newInvoice.invoiceType')
                            }}
                            onChange={(e) => handleInputChange("idInvoiceType", e.target.value)}
                            onBlur={(e) => handleInputChange("idInvoiceType", e.target.value)}
                            error={errors?.idInvoiceType ? true : false}
                        >
                            <MenuItem disabled value="">
                                <em>{intl.get('newInvoice.invoiceType')}</em>
                            </MenuItem>
                            {invoiceTypes?.map((invoiceType) => (
                                <MenuItem
                                    key={invoiceType.id}
                                    value={invoiceType.id}
                                >
                                    {invoiceType.name}
                                </MenuItem>
                            ))}
                            </Select>
                        )}
                    </Grid>
                </Grid>
                <Grid container xl={6}  className='justify-content-end'>
                    <Grid container xl={10} className='justify-content-start'>
                        <Typography className={`${errors?.paymentTerms ? 'text-danger' : ''}`} style={{marginTop: "5px"}}>
                            {intl.get('newInvoice.paymentTerms')} {errors?.paymentTerms && errors?.paymentTerms !== "missing confidence" && (<span className='text-danger'>{errors?.paymentTerms}</span>)}
                        </Typography>
                    </Grid>
                    <Grid container xl={10} className='justify-content-end'>
                        {view ? (
                            <input 
                                type="text"
                                value={input?.paymentTerms}
                                disabled={true}
                                style={{width: '100%', padding: '8px'}}
                            />
                        ) : (
                        <TextField
                            id="paymentTerms"
                            name="paymentTerms"
                            placeholder={intl.get('newInvoice.paymentTerms')}
                            value={input?.paymentTerms}
                            onChange={(e) => handleInputChange("paymentTerms", e.target.value)}
                            onBlur={(e) => handleInputChange("paymentTerms", e.target.value)}
                            disabled={disabled}
                            fullWidth
                            error={errors?.paymentTerms ? true : false}
                        />
                        )}
                    </Grid>
                </Grid>
            </Grid>
            {errors?.invoiceItems && (
                <Grid container xl={12} className='justify-content-start' style={{marginTop: "10px", marginBottom: "-10px"}}>
                    <Typography className='text-danger'>
                        {errors?.invoiceItems}
                    </Typography>
                </Grid>
            )}
            <Grid xl={12} style={{marginTop: "10px"}}>
                {view ? (
                    <NewInvoiceItemTableView />
                ) : (
                    <NewInvoiceItemTable disabled={disabled}/>
                )}
            </Grid>
            <Grid container  xl={12} style={{marginTop: "10px"}}>
                <Grid xl={4}>
                </Grid>
                <Grid xl={8}>
                    {view ? (
                        <NewInvoiceSummaryView />
                    ) : (
                        <NewInvoiceSummary disabled={disabled} />
                    )}
                </Grid>
            </Grid>
            <Grid className='justify-content-end'>
                <Grid className="d-flex justify-content-end">
                    <WorkflowActionsBar
                        viewMode=''
                        onActionClick={handleSave}
                        id={input.id}
                        stateId={input.idWorkflowState}
                        loading={loading}
                        disabled={isDisabled()}
                    />
                </Grid>
            </Grid>
        </>
    )
}

export default NewInvoiceForm;
